// useCmdkHistory — CommandPalette 최근 사용 기록 훅 (U49)
//
// 책임:
//   - localStorage('locker:cmdk:recent')에 최근 사용 명령/검색/이동 5개 LRU 저장
//   - 중복 제거 (kind + payload 기준)
//   - push / clear / remove 연산 제공
//
// 설계 결정 (E3):
//   - localStorage 채택 (sessionStorage는 탭 간 공유 안 됨 — 단일 마스터 환경에 부적합)
//   - 최대 5개 LRU — 가장 최근 항목이 index 0

import { useState, useCallback } from 'react';

// ─── 타입 정의 ───────────────────────────────────────────────

export type RecentEntryKind = 'command' | 'search' | 'navigate';

export interface RecentEntry {
  kind: RecentEntryKind;
  label: string;
  payload: {
    commandId?: string;
    query?: string;
    itemId?: number;
    groupId?: number;
    tagName?: string;
  };
  timestamp: number;
}

// ─── 상수 ────────────────────────────────────────────────────

export const CMDK_HISTORY_KEY = 'locker:cmdk:recent';
export const CMDK_HISTORY_MAX = 5;

// ─── 직렬화 유틸 ─────────────────────────────────────────────

/**
 * localStorage에서 history 배열을 읽어 반환.
 * 파싱 실패 시 빈 배열 반환.
 */
export function readHistory(): RecentEntry[] {
  try {
    const raw = localStorage.getItem(CMDK_HISTORY_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as RecentEntry[];
  } catch {
    return [];
  }
}

/**
 * history 배열을 localStorage에 저장.
 */
export function writeHistory(entries: RecentEntry[]): void {
  try {
    localStorage.setItem(CMDK_HISTORY_KEY, JSON.stringify(entries));
  } catch {
    // localStorage 쓰기 실패 (private 모드, 용량 초과 등) — 무시
  }
}

// ─── 중복 판별 ────────────────────────────────────────────────

/**
 * 두 항목이 동일한 명령/검색/이동인지 판별.
 * kind와 payload의 각 필드를 비교한다.
 */
export function isSameEntry(a: RecentEntry, b: RecentEntry): boolean {
  if (a.kind !== b.kind) return false;
  const ap = a.payload;
  const bp = b.payload;
  return (
    ap.commandId === bp.commandId &&
    ap.query === bp.query &&
    ap.itemId === bp.itemId &&
    ap.groupId === bp.groupId &&
    ap.tagName === bp.tagName
  );
}

// ─── LRU push 순수 함수 ───────────────────────────────────────

/**
 * 기존 entries에 newEntry를 LRU 방식으로 추가한 새 배열을 반환.
 *
 * 동작:
 *   1. 동일 항목(isSameEntry) 제거
 *   2. 맨 앞(index 0)에 새 항목 삽입
 *   3. CMDK_HISTORY_MAX 초과분 잘라냄
 */
export function pushEntry(entries: RecentEntry[], newEntry: RecentEntry): RecentEntry[] {
  // 중복 제거
  const filtered = entries.filter((e) => !isSameEntry(e, newEntry));
  // 최신 항목을 맨 앞에
  const updated = [newEntry, ...filtered];
  // 최대 5개 제한
  return updated.slice(0, CMDK_HISTORY_MAX);
}

// ─── 훅 반환 타입 ─────────────────────────────────────────────

export interface UseCmdkHistoryReturn {
  /** 최근 사용 목록 (최대 5개, index 0 = 가장 최근) */
  entries: RecentEntry[];
  /** 새 항목 추가 (LRU, 중복 제거) */
  push: (entry: Omit<RecentEntry, 'timestamp'>) => void;
  /** 전체 초기화 */
  clear: () => void;
  /** 특정 인덱스 항목 제거 */
  remove: (idx: number) => void;
}

// ─── 훅 구현 ─────────────────────────────────────────────────

/**
 * CommandPalette 최근 사용 기록 훅.
 * localStorage를 영속 저장소로 사용한다.
 */
export function useCmdkHistory(): UseCmdkHistoryReturn {
  // 초기 상태는 localStorage에서 로드
  const [entries, setEntries] = useState<RecentEntry[]>(() => readHistory());

  const push = useCallback((entry: Omit<RecentEntry, 'timestamp'>) => {
    const newEntry: RecentEntry = { ...entry, timestamp: Date.now() };
    setEntries((prev) => {
      const updated = pushEntry(prev, newEntry);
      writeHistory(updated);
      return updated;
    });
  }, []);

  const clear = useCallback(() => {
    setEntries([]);
    writeHistory([]);
  }, []);

  const remove = useCallback((idx: number) => {
    setEntries((prev) => {
      if (idx < 0 || idx >= prev.length) return prev;
      const updated = prev.filter((_, i) => i !== idx);
      writeHistory(updated);
      return updated;
    });
  }, []);

  return { entries, push, clear, remove };
}
